import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import Select from "react-select";
import { Button } from "../../Button";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";
import { useAuth } from "../../../context/AuthContext";
import { Business } from "../../../api/business";
import {
  createSpecialPartner,
  updateSpecialPartner,
  CashbackType,
  SpecialPartnerViewModel,
} from "../../../api/specialPartners";

interface AddEditSpecialPartnerModalProps {
  isOpen: boolean;
  partner: SpecialPartnerViewModel | null;
  onClose: () => void;
  onSuccess: (partnerName: string, isEdit: boolean) => void;
}

const EMPTY_FORM = {
  campaignName: "",
  contentfulBusinessId: "",
  cashbackType: "Percentage" as CashbackType,
  totalCashback: "",
  userCashback: "",
  fixedAmount: "",
};

const AddEditSpecialPartnerModal: React.FC<AddEditSpecialPartnerModalProps> = ({
  isOpen,
  partner,
  onClose,
  onSuccess,
}) => {
  const { cachedCompanies, fetchCompanies, cachedMerchants, fetchMerchants } = useAuth();

  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Load companies on mount
  useEffect(() => {
    if (!cachedCompanies.length) fetchCompanies();
  }, []);

  // Load businesses when company is selected
  useEffect(() => {
    if (!selectedCompanyId) {
      setBusinesses([]);
      return;
    }
    const load = async () => {
      setLoadingBusinesses(true);
      try {
        const result = await fetchMerchants(selectedCompanyId);
        setBusinesses(result);
      } catch {
        setBusinesses([]);
      } finally {
        setLoadingBusinesses(false);
      }
    };
    // Use cached if available
    if (cachedMerchants[selectedCompanyId]) {
      setBusinesses(cachedMerchants[selectedCompanyId]);
    } else {
      load();
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    if (partner) {
      setForm({
        campaignName: partner.campaignName,
        contentfulBusinessId: partner.contentfulBusinessId ?? "",
        cashbackType: partner.cashbackType,
        totalCashback: String(partner.totalCashback),
        userCashback: String(partner.userCashback),
        fixedAmount: partner.fixedAmount != null ? String(partner.fixedAmount) : "",
      });
    } else {
      setForm(EMPTY_FORM);
      setSelectedCompanyId(null);
      setBusinesses([]);
    }
    setErrors({});
    setApiError(null);
  }, [partner, isOpen]);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!form.contentfulBusinessId) newErrors.business = "Please select a business";
    const total = parseFloat(form.totalCashback);
    const user = parseFloat(form.userCashback);
    if (isNaN(total) || total <= 0) newErrors.totalCashback = "Must be a positive number";
    if (isNaN(user) || user <= 0) newErrors.userCashback = "Must be a positive number";
    if (!isNaN(user) && !/^\d+(\.\d{1,2})?$/.test(form.userCashback)) newErrors.userCashback = "Max 2 decimal places";
    if (!isNaN(total) && !isNaN(user) && user > total)
      newErrors.userCashback = "Cannot exceed total cashback";
    if (form.cashbackType === "Percentage") {
      if (!isNaN(total) && total > 100) newErrors.totalCashback = "Cannot exceed 100%";
      if (!isNaN(user) && user > 100) newErrors.userCashback = "Cannot exceed 100%";
    }
    return newErrors;
  };

  const handleSubmit = async () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const total = parseFloat(form.totalCashback);
    const user = parseFloat(form.userCashback);
    const fixedAmt = form.fixedAmount.trim() ? parseFloat(form.fixedAmount) : null;

    setLoading(true);
    setApiError(null);
    try {
      if (partner) {
        await updateSpecialPartner({
          id: partner.id,
          campaignName: form.campaignName,
          contentfulBusinessId: form.contentfulBusinessId,
          cashbackType: form.cashbackType,
          totalCashback: total,
          userCashback: user,
          fixedAmount: fixedAmt,
        });
        onSuccess(form.campaignName, true);
      } else {
        await createSpecialPartner({
          campaignName: form.campaignName,
          contentfulBusinessId: form.contentfulBusinessId,
          cashbackType: form.cashbackType,
          totalCashback: total,
          userCashback: user,
          fixedAmount: fixedAmt,
        });
        onSuccess(form.campaignName, false);
      }
    } catch (err: any) {
      setApiError(err?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const cashbackUnit = form.cashbackType === "Percentage" ? "%" : "DKK";

  const companyOptions = cachedCompanies.map((c) => ({ value: c.id, label: c.name }));
  const businessOptions = businesses.map((b) => ({ value: b.id, label: b.name }));
  const selectedBusinessOption = form.contentfulBusinessId
    ? businessOptions.find((o) => o.value === form.contentfulBusinessId) ?? null
    : null;

  return (
    <Modal
      className="modal-content md:max-w-[480px]"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel={partner ? "Edit Offline Campaign" : "Add Offline Campaign"}
    >
      <div className="flex justify-between mb-6">
        <h2 className="text-primary font-medium text-xl">
          {partner ? "Edit Offline Campaign" : "Add Offline Campaign"}
        </h2>
        <Button onClick={onClose} variant="icon" disabled={loading}>
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      <div className="space-y-4">
        {apiError && (
          <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
            {apiError}
          </div>
        )}

        {/* When editing, show the selected business as read-only */}
        {partner ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business
            </label>
            <div className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-700">
              {partner.campaignName}
            </div>
          </div>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company
              </label>
              <Select
                options={companyOptions}
                value={companyOptions.find((o) => o.value === selectedCompanyId) ?? null}
                onChange={(opt) => {
                  setSelectedCompanyId(opt?.value ?? null);
                  setForm((f) => ({ ...f, campaignName: "", contentfulBusinessId: "" }));
                }}
                placeholder="Select company..."
                isClearable
                className="basic-select"
                classNamePrefix="select"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business
              </label>
              <Select
                options={businessOptions}
                value={selectedBusinessOption}
                onChange={(opt) => {
                  const business = businesses.find((b) => b.id === opt?.value) ?? null;
                  setForm((f) => ({
                    ...f,
                    contentfulBusinessId: business?.id ?? "",
                    campaignName: business?.name ?? "",
                  }));
                  if (business) setErrors((e) => ({ ...e, business: "" }));
                }}
                isDisabled={!selectedCompanyId}
                isLoading={loadingBusinesses}
                placeholder={selectedCompanyId ? "Select business..." : "Select company first"}
                isClearable
                className="basic-select"
                classNamePrefix="select"
              />
              {errors.business && (
                <p className="text-red-500 text-xs mt-1">{errors.business}</p>
              )}
            </div>
          </>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cashback Type
          </label>
          <div className="flex gap-3">
            {(["Percentage", "Fixed"] as CashbackType[]).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setForm((f) => ({ ...f, cashbackType: type }))}
                className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  form.cashbackType === type
                    ? "bg-primary text-white border-primary"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {type === "Percentage" ? "Percentage (%)" : "Fixed (DKK)"}
              </button>
            ))}
          </div>
        </div>

        {form.cashbackType === "Fixed" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fixed Transaction Amount (DKK) <span className="text-gray-400 font-normal">— optional</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.fixedAmount}
              onChange={(e) => setForm((f) => ({ ...f, fixedAmount: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g. 200 — if set, amount column is not required in CSV"
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total Cashback ({cashbackUnit})
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.totalCashback}
              onChange={(e) =>
                setForm((f) => ({ ...f, totalCashback: e.target.value }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="0"
            />
            {errors.totalCashback && (
              <p className="text-red-500 text-xs mt-1">{errors.totalCashback}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              User Cashback ({cashbackUnit})
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.userCashback}
              onChange={(e) =>
                setForm((f) => ({ ...f, userCashback: e.target.value }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="0"
            />
            {errors.userCashback && (
              <p className="text-red-500 text-xs mt-1">{errors.userCashback}</p>
            )}
          </div>
        </div>

        <div className="flex gap-3 justify-end pt-4">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={loading}>
            {partner ? "Save Changes" : "Add Campaign"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddEditSpecialPartnerModal;
